import {deepCopy} from '../utils/general';
import {
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
  type MouseEvent,
  type KeyboardEvent,
} from 'react';
// import useComponentSize from '@rehooks/component-size';
import useMeasure from 'react-use-measure';
import {
  getCornerSize,
  isValidWorldState,
  makeRowColKey,
  xyToRowCol,
  getValidKarelIdx,
  type KarelEnvironment,
} from './util';
import {Agent} from './worldComponents/Agent';
import {Beepers} from './worldComponents/Beepers';
import {EditableWalls} from './worldComponents/EditableWalls';
import {GridBackground} from './worldComponents/GridBackground';
import {Paint} from './worldComponents/Paint';
import styled from 'styled-components';
import type {IKarelState, KarelDirection} from './karelInterfaces';
import type {KarelType} from './util';

interface WorldEditorProps {
  worldState: IKarelState;
  setWorldState: Dispatch<SetStateAction<IKarelState>>;
  isEditable: boolean;
  karelType?: KarelType;
}

export const WorldEditor = ({
  worldState,
  setWorldState,
  isEditable,
  karelType = 'classic',
}: WorldEditorProps) => {
  const [editType, setEditType] = useState('moveKarel');

  if (!worldState) {
    return <></>;
  }

  function setState(key, value) {
    setWorldState((oldWorldState: IKarelState): IKarelState => {
      const newWorldState = deepCopy(oldWorldState);
      newWorldState[key] = value;
      newWorldState['karelCol'] = getValidKarelIdx(
        newWorldState['nCols'],
        newWorldState['karelCol'],
      );
      newWorldState['karelRow'] = getValidKarelIdx(
        newWorldState['nRows'],
        newWorldState['karelRow'],
      );

      if (isValidWorldState(newWorldState)) {
        return newWorldState;
      } else {
        return oldWorldState;
      }
    });
  }

  function rowColClick(row: number, col: number): void {
    if (editType == 'moveKarel') {
      const change = row != worldState.karelRow || col != worldState.karelCol;
      if (change) {
        setWorldState(oldWorldState => {
          const newWorldState = deepCopy(oldWorldState);
          newWorldState['karelRow'] = row;
          newWorldState['karelCol'] = col;
          if (isValidWorldState(newWorldState)) {
            return newWorldState;
          } else {
            return oldWorldState;
          }
        });
      } else {
        setState('karelDir', rotateLeft(worldState.karelDir));
      }
    }
    if (editType == 'addBeeper') {
      editBeepers(setWorldState, row, col, +1);
    }
    if (editType == 'removeBeeper') {
      editBeepers(setWorldState, row, col, -1);
    }
  }

  return (
    <EditableOuter>
      <EditableCanvas
        rowColClick={rowColClick}
        worldState={worldState}
        setWorldState={setWorldState}
        isEditable={isEditable}
        karelType={karelType}
      />
      <EditButtons
        editType={editType}
        setEditType={setEditType}
        worldState={worldState}
        setState={setState}
        isEditable={isEditable}
      />
    </EditableOuter>
  );
};

interface EditButtonsProps {
  editType: string;
  setEditType: (value: string) => void;
  worldState: IKarelState;
  setState: (key: string, value: number) => void;
  isEditable: boolean;
}

export const EditButtons = ({
  editType,
  setEditType,
  worldState,
  setState,
  isEditable,
}: EditButtonsProps) => {
  return (
    <div className="d-flex flex-row align-items-center">
      <div className="mr-1 d-flex align-items-center">
        <IntInput
          label="rows"
          key="rows"
          value={worldState.nRows}
          setValue={e => {
            setState('nRows', e);
          }}
          isEditable={isEditable}
        />{' '}
        x
        <IntInput
          label="cols"
          key="cols"
          value={worldState.nCols}
          setValue={e => { setState('nCols', e); }}
          isEditable={isEditable}
        />
      </div>

      <EditTypeRadio
        editType={editType}
        setEditType={setEditType}
        value="moveKarel"
        label={<>Karel</>}
        isEditable={isEditable}
      />
      <EditTypeRadio
        editType={editType}
        setEditType={setEditType}
        value="addBeeper"
        label={<BeeperIcon text="+" />}
        isEditable={isEditable}
      />
      <EditTypeRadio
        editType={editType}
        setEditType={setEditType}
        value="removeBeeper"
        label={<BeeperIcon text="-" />}
        isEditable={isEditable}
      />
    </div>
  );
};

interface EditableCanvasProps {
  rowColClick: (row: number, col: number) => void;
  worldState: IKarelState;
  setWorldState: Dispatch<SetStateAction<IKarelState>>;
  isEditable: boolean;
  karelType: KarelType;
}

const EditableCanvas = ({
  rowColClick,
  worldState,
  setWorldState,
  isEditable,
  karelType,
}: EditableCanvasProps) => {
  const [canvasRef, canvasState] = useMeasure();

  // package the state into a variable
  const state: KarelEnvironment = {
    // worldState is karel relevant variables. This is currently
    // a fixed example :-)
    worldState,
    // canvas state are {width, height, ref} of the canvas
    canvasState,
  };

  const worldHeight = useMemo(() => {
    const cornerSize = getCornerSize({worldState, canvasState});
    return worldState.nRows * cornerSize;
  }, [canvasState, worldState]);

  function onClick(e: MouseEvent<HTMLDivElement>) {
    if (!isEditable) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const [row, col] = xyToRowCol(state, x, y);
    rowColClick(row, col);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!isEditable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
    }
  }

  if (!isValidWorldState(worldState)) return <>Invalid Karel state</>;

  return (
    <div
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="grid"
      aria-label="Karel world editor"
      tabIndex={0}
      ref={canvasRef}
      className="w-100"
      style={{
        height: worldHeight,
        backgroundColor: 'white',
        position: 'relative',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <GridBackground state={state} />
      <Paint state={state} />
      <EditableWalls
        state={state}
        setWorldState={setWorldState}
        isEditable={isEditable}
      />
      <Beepers state={state} />
      <Agent state={state} karelType={karelType} />
    </div>
  );
};

interface IntInputProps {
  value: number;
  setValue: (value: number) => void;
  label: string;
  isEditable: boolean;
}

const IntInput = ({value, setValue, label, isEditable}: IntInputProps) => {
  return (
    <>
      <div className="input-group input-group-sm" style={{maxWidth: 50}}>
        {/* <span className="input-group-text" id="inputGroup-sizing-sm">{label}</span> */}
        <input
          key={label}
          onChange={e => { setValue(Number(e.target.value)); }}
          value={value}
          type="number"
          min={1}
          max={100}
          disabled={!isEditable}
          className="form-control"
          aria-label="Sizing example input"
          aria-describedby="inputGroup-sizing-sm"
        />
      </div>
    </>
  );
};
const EditTypeRadio = ({value, label, editType, setEditType, isEditable}) => {
  function onClick() {
    setEditType(value);
  }

  const btnColor = editType === value ? 'btn-outline-secondary' : 'btn-light';
  return (
    <button
      onClick={() => { onClick(); }}
      className={`btn btn-sm ${btnColor}`}
      disabled={!isEditable}
    >
      {label}
    </button>
  );
};

function rotateLeft(dir: KarelDirection): KarelDirection {
  switch (dir) {
    case 'East':
      return 'North';
    case 'North':
      return 'West';
    case 'West':
      return 'South';
    case 'South':
      return 'East';
    default:
      return 'East';
  }
}

function editBeepers(
  setWorldState: Dispatch<SetStateAction<IKarelState>>,
  row: number,
  col: number,
  delta: number,
): void {
  const rowColKey = makeRowColKey(row, col);
  setWorldState(oldWorldState => {
    const newWorldState = deepCopy(oldWorldState);
    // careful about getting the new beepers state
    if (!('beepers' in newWorldState)) {
      newWorldState['beepers'] = {};
    }
    const beepers = newWorldState['beepers'];
    const oldCount = rowColKey in beepers ? beepers[rowColKey] : 0;
    const newCount = oldCount + delta;
    if (newCount >= 0) {
      beepers[rowColKey] = newCount;
    }

    return newWorldState;
  });
}

interface BeeperIconProps {
  text: string;
}

const BeeperIcon = ({text}: BeeperIconProps) => {
  const size = 24;
  return (
    <div
      className="karel-stone"
      style={{
        width: size,
        height: size,
        marginLeft: 0,
        marginTop: 0,
        position: 'relative',
      }}
    >
      <div className="karel-stoneDiamond" />
      <span
        className="karel-stoneText"
        style={{
          fontSize: size * 0.75,
          marginTop: -size * 1.05,
          width: size,
          marginLeft: -size * 0.5,
        }}
      >
        {text}
      </span>
    </div>
  );
};

const EditableOuter = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;
